import type { AxiosError } from 'axios';

export function isAxiosError(val: unknown): val is AxiosError {
  if (val != null && typeof val === 'object') {
    const pae = val as Partial<AxiosError>;
    return Boolean(pae.isAxiosError || pae.response);
  }
  return false;
}

export function httpErrorStatus(val: unknown): number | undefined {
  if (!val) {
    return undefined;
  }
  /*
    Axios has status in val.response.status, while fetch() has it in
    val.status.
    For aws sdk errors, it is in val.$metadata.httpStatusCode.
  */
  const v = val as Record<string, unknown>;
  const response = v.response as Record<string, unknown> | undefined;
  const metadata = v.$metadata as Record<string, unknown> | undefined;
  const status = response?.status ?? v.status ?? metadata?.httpStatusCode;
  if (typeof status === 'number') {
    return status;
  }

  return undefined;
}

/**
 * Extracts the error data payload from an axios error response.
 * Returns undefined if the error is not an axios error or has no response data.
 * The returned value is narrowed to a record if it is an object, otherwise undefined.
 */
export function axiosErrorData(err: unknown): Record<string, unknown> | undefined {
  if (!isAxiosError(err)) return undefined;
  const data: unknown = err.response?.data;
  if (data != null && typeof data === 'object') {
    return data as Record<string, unknown>;
  }
  return undefined;
}

/**
 * Extracts a nested `error.message` string from an axios error's response data,
 * following the common `{ error: { message: string } }` server error format.
 */
export function axiosErrorMessage(err: unknown): string | undefined {
  const data = axiosErrorData(err);
  if (data == null) return undefined;
  const errorObj = data.error;
  if (errorObj != null && typeof errorObj === 'object') {
    const msg = (errorObj as Record<string, unknown>).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}

/**
 * Extracts a nested `error.statusCode` number from an axios error's response data.
 */
export function axiosErrorStatusCode(err: unknown): number | undefined {
  const data = axiosErrorData(err);
  if (data == null) return undefined;
  const errorObj = data.error;
  if (errorObj != null && typeof errorObj === 'object') {
    const code = (errorObj as Record<string, unknown>).statusCode;
    if (typeof code === 'number') return code;
  }
  return undefined;
}

export function axiosErrorPayload(err: unknown): Record<string, unknown> | undefined {
  const data = axiosErrorData(err);
  if (data == null) return undefined;
  const errorObj = data.error;
  if (errorObj != null && typeof errorObj === 'object') {
    const payload = (errorObj as Record<string, unknown>).payload;
    if (payload != null && typeof payload === 'object') {
      return payload as Record<string, unknown>;
    }
  }
  return undefined;
}



